/* eslint-disable max-params */
import { useEffect } from 'react';

/**
 * React hook to handle messages from a child iframe, filtering by message type and allowed origins.
 *
 * @param {string} type - The type or action of the message to listen for. The hook will trigger the callback if `event.data.type`, `event.data`, or `event.data.action` matches this value.
 * @param {(e: MessageEvent) => void} cb - Callback function to execute when a matching message is received. The callback receives the message payload if available, otherwise the entire message data.
 * @param {string[]} origin - Array of allowed origins. The message will only be processed if its origin is included in this array, or if the first element is '*', which allows all origins.
 * @param {unknown[]} [dependencies=[]] - Optional array of dependencies for the effect. The event listener will be re-registered when any dependency changes.
 *
 * @example
 * useIframeMessage(
 *   'update',
 *   (payload) => {
 *     console.log('Received update payload:', payload);
 *   },
 *   ['https://trusted-origin.com']
 * );
 */
export const useIframeMessage = (
  type: string,
  cb: (e: MessageEvent) => void,
  origin: string[],
  dependencies: unknown[] = []
) => {
  useEffect(() => {
    function messageEventHandler(event: MessageEvent) {
      if (origin.includes(event.origin) === false && origin[0] !== '*') {
        return;
      }

      /* eslint-disable */
      if (cb && (event.data.type === type || event.data === type || event.data.action === type)) {
        cb(event.data.payload ? event.data.payload : event.data);
      }
      /* eslint-enable */
    }

    window.addEventListener('message', messageEventHandler);

    return () => {
      window.removeEventListener('message', messageEventHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};
